import { Typography } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import GoToPage, { GTPProps } from "../GoToPage/GoToPage";
import { useReduxState } from "../../../explorer/redux/hooks";
import { Machine, Site } from "../../../explorer/types/SpareParts";
import { reduxStoreConfig } from "../../configuration/reduxStoreConfig";
import { useEffect, useState } from "react";
import ServiceProvider from "../../configuration/ServiceProvider";
import {
    AppIconsHomeStyled,
    BreadcrumbsStyled,
} from "./BreadcrumbComponent.styles";
import { BomItemDetails } from "../../../explorer/types/SpareParts/BomItemDetails";
import { useTranslation } from "../../../explorer/hooks/useTranslation";
import useBrands from "../../../explorer/hooks/useBrands";
import useLanguages from "../../../explorer/hooks/useLanguages";
import useCurrentSite from "../../hook/useCurrentSite";
import { MachineMoreType } from "../../pages/SpareParts/MachineMorePage/MachineMorePage";

type TBreadCrumbsButton = {
    gtp: GTPProps;
    label: React.ReactNode;
};

const BreadcrumbComponent = () => {
    const { customerSiteCode, machineId, tabIndex, codeSearched } = useParams();
    const naturalKey = useParams()["*"];
    const brand = useBrands().currentBrandUrl;
    const lang = useLanguages().currentLanguage;

    const [currentSite] = useCurrentSite();
    const [currentMachine] = useReduxState<Machine>(
        reduxStoreConfig.sparePartsCurrentMachine
    );
    const [bomItems, setBomItems] = useState<BomItemDetails[]>([]);
    const [breadCrumbsButton, setBreadCrumbsButton] = useState<
        TBreadCrumbsButton[]
    >([]);

    const location = useLocation();

    const { t } = useTranslation();

    // Recupera BomItems
    useEffect(() => {
        if (machineId !== undefined) {
            ServiceProvider.SpareParts.getMachineBOMById(
                brand,
                machineId,
                lang
            ).then((result) => {
                setBomItems(result);
            });
        }
    }, [machineId, lang]);


    useEffect(() => {
        const breadCrumbsButtonTemp: TBreadCrumbsButton[] = [];

        if (customerSiteCode && currentSite) {
            breadCrumbsButtonTemp.push({
                gtp: {
                    page: "spareparts.installedbase",
                    params: { customerSiteCode },
                },
                label: `${currentSite.customerSiteCode} ${currentSite.customerSiteName}`,
            });
        }

        if (machineId) {
            breadCrumbsButtonTemp.push({
                gtp: {
                    page: "spareparts.machine",
                    params: {
                        customerSiteCode: customerSiteCode,
                        machineId: machineId,
                        naturalKey: currentMachine.naturalKey,
                    },
                },
                label: `${currentMachine.serialNumber} ${currentMachine.description}`,
            });
        }

        if (naturalKey && bomItems.length !== 0) {
            const nks = naturalKey.split("/");

            for (let i = 0; i < nks.length; i++) {
                const nk: string = nks.slice(0, i + 1).join("/");
                const bomItem: BomItemDetails = bomItems.filter(
                    (x) => x.naturalKey === nk
                )[0];

                if (bomItem)
                    breadCrumbsButtonTemp.push({
                        gtp: {
                            page: "spareparts.machine",
                            params: {
                                customerSiteCode: customerSiteCode,
                                machineId: machineId,
                                naturalKey: nk,
                            },
                        },
                        label: `${bomItem.item} ${bomItem.description ?? ""}`,
                    });
            }
        }

        if (tabIndex) {
          breadCrumbsButtonTemp.push({
            gtp: {
              page: "spareparts.machineMore",
              params: { customerSiteCode, machineId, tabIndex },
            },
            label: <LabelComponent label={
              tabIndex === MachineMoreType.TechnicalDocumentation
                ? "common.spareparts.thecnicalDoc"
                : tabIndex === MachineMoreType.EmergencyList
                ? "common.spareparts.emergList"
                : tabIndex === MachineMoreType.MaintenanceList
                ? "common.spareparts.maintList"
                : tabIndex} />
          });
        }

        if (codeSearched) {
            breadCrumbsButtonTemp.push({
                gtp: { page: "spareparts.goToSearch", params: { code: "" } },
                label: <><LabelComponent label="common.spareparts.partSearchresult"/>: {codeSearched.toUpperCase()}</>,
            });
        }

        if (location.pathname.endsWith("/basket")) {
            breadCrumbsButtonTemp.push({
                gtp: { page: "system.goToBasket" },
                label: <LabelComponent label="common.basket.shoppingBasket" />,
            });
        }

        if (location.pathname.endsWith("/offerOrderHistory")) {
            breadCrumbsButtonTemp.push({
                gtp: { page: "system.offerOrderHistory" },
                label: <LabelComponent label="common.offerorder.offerOrderHistory" />,
            });
        }

        setBreadCrumbsButton(breadCrumbsButtonTemp);
    }, [location, currentSite, bomItems, lang]);

    return (
        <BreadcrumbsStyled
            aria-label="breadcrumb"
            separator="/"
            sx={{ margin: "16px 0" }}
            maxItems={6}
            itemsAfterCollapse={2}
        >
            {/* Iconcina dell'Home */}
            <GoToPage page="home">
                <AppIconsHomeStyled />
            </GoToPage>

            {/* Tutti glia ltri campi presi dal params */}
            {breadCrumbsButton.map((crumb, index) => {
                const isLast = index === breadCrumbsButton.length - 1;
                return isLast ? (
                    <Typography key={crumb.gtp.page} color="text.primary">
                        {crumb.label}
                    </Typography>
                ) : (
                    <GoToPage
                        key={crumb.gtp.page}
                        page={crumb.gtp.page}
                        params={crumb.gtp.params}
                    >
                        {crumb.label}
                    </GoToPage>
                );
            })}
        </BreadcrumbsStyled>
    );
};

type LabelProps = {
    label: string;
}

const LabelComponent = (props: LabelProps) => {
    const {t} = useTranslation();
    return <span>{t(props.label)}</span>;
}

export default BreadcrumbComponent;
